import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SIDEKICK_DOCK_OVERLAY_ALWAYS_PX } from '../../constants';
import { SidekickApiProvider } from '../../contexts/SidekickApiProvider';
import { SidekickConfigProvider } from '../../contexts/SidekickConfigProvider';
import { SidekickFeatureEventsProvider } from '../../featureEvents';
import { useIsSidekickEnabled, useIsSidekickM1Enabled } from '../../flag';
import { useSidekickActingOrganization } from '../../hooks/useSidekickActingOrganization';
import { useSidekickPackageApi } from '../../hooks/useSidekickPackageApi';
import { useSidekickLifecycleEvents } from '../../lifecycle';
import { closePanel, selectIsPanelOpen } from '../../state/aiSidekickSlice';
import AISidekickPanel from '../AISidekickPanel';
import SidekickDock from '../SidekickDock';
import SidekickErrorBoundary from '../SidekickErrorBoundary';
import type { SidekickDockShellProps } from './types';

/**
 * Host integration point for the docked Sidekick: composes the dock,
 * the drawer it opens into, the package providers, and a shared error
 * boundary behind one component.
 *
 * Unlike `SidekickLayoutShell`, this shell renders no layout of its
 * own. Both surfaces are fixed-position, so the host mounts it once
 * outside its router and the dock survives navigation. Whether the
 * drawer overlays or expects room made for it is the host's call, via
 * `overlayBelowPx`.
 *
 * Renders nothing unless both the Sidekick flag and its M1 flag are
 * on, so hosts do not need to guard the mount site.
 *
 * Any API callback the host omits falls back to a package default
 * backed by the package's own generated client, so a host that has
 * registered `sidekickRtkClient` gets a working conversation, feedback,
 * and consent path from the mount alone.
 */
function SidekickDockShell({
  apiBaseUrl,
  chips,
  isChipsLoading,
  fleetId,
  interfaceName,
  actingOrganizationId,
  ensureConversation,
  submitFeedback,
  getConsentStatus,
  acceptConsent,
  searchRecipients,
  onNavigate,
  timeZone,
  locale,
  userId,
  topOffsetPx,
  overlayBelowPx = SIDEKICK_DOCK_OVERLAY_ALWAYS_PX,
  onNewConversation,
  onArtifactDownloaded,
  onLifecycleEvent,
  onFeatureUsed,
  onPanelError,
  ...dockProps
}: SidekickDockShellProps) {
  const dispatch = useDispatch();
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const isSidekickEnabled = useIsSidekickEnabled();
  const isSidekickM1Enabled = useIsSidekickM1Enabled();
  const sidekickApi = useSidekickPackageApi({
    ensureConversation,
    submitFeedback,
    getConsentStatus,
    acceptConsent,
    userId,
  });
  useSidekickActingOrganization(actingOrganizationId);
  useSidekickLifecycleEvents(
    isSidekickEnabled && isSidekickM1Enabled ? onLifecycleEvent : undefined
  );
  const chipSet = useMemo(() => ({ questions: chips }), [chips]);

  const handleClose = useCallback(() => {
    dispatch(closePanel());
  }, [dispatch]);

  if (!isSidekickEnabled || !isSidekickM1Enabled) {
    return null;
  }

  return (
    <SidekickApiProvider
      apiBaseUrl={apiBaseUrl}
      fleetId={fleetId}
      interfaceName={interfaceName}
      searchRecipients={searchRecipients}
      {...sidekickApi}
    >
      <SidekickConfigProvider
        timeZone={timeZone}
        locale={locale}
        userId={userId}
      >
        <SidekickErrorBoundary onError={onPanelError}>
          <SidekickFeatureEventsProvider onFeatureUsed={onFeatureUsed}>
            <SidekickDock
              chips={chips}
              isChipsLoading={isChipsLoading}
              {...dockProps}
            />
            <AISidekickPanel
              open={isPanelOpen}
              onClose={handleClose}
              topOffsetPx={topOffsetPx}
              overlayBelowPx={overlayBelowPx}
              onNewConversation={onNewConversation}
              onArtifactDownloaded={onArtifactDownloaded}
              chipSet={chipSet}
              isChipSetLoading={isChipsLoading}
              onNavigate={onNavigate}
            />
          </SidekickFeatureEventsProvider>
        </SidekickErrorBoundary>
      </SidekickConfigProvider>
    </SidekickApiProvider>
  );
}

export default SidekickDockShell;
